import {
  GeneralPermissions,
  ImsPermissions,
  UserPermissions,
} from "@redotech/account-common/permissions";
import { useContext } from "react";
import { PermissionsContext } from "../auth/permissions-context";
import { TeamContext } from "../team";

/** Every Catalog data RPC in the inventory server requires this permission. */
export const CATALOG_DATA_PERMISSION = GeneralPermissions.OTHER;

interface CatalogAccessTeam {
  settings: { ims?: { catalogEnabled?: boolean } };
}

export interface CatalogAccessInput {
  team: CatalogAccessTeam | undefined;
  userPermissions: ReadonlySet<UserPermissions> | undefined;
}

/** Product and family details (pages and modals); Catalog need not be enabled. */
export function canViewCatalogProductDetails(
  userPermissions: ReadonlySet<UserPermissions> | undefined,
): boolean {
  return userPermissions?.has(CATALOG_DATA_PERMISSION) ?? false;
}

/** Whether the Catalog section (nav, list pages, announcement) is usable. */
export function canAccessCatalog({
  team,
  userPermissions,
}: CatalogAccessInput): boolean {
  return (
    team?.settings.ims?.catalogEnabled === true &&
    canViewCatalogProductDetails(userPermissions)
  );
}

/** Bills of materials live in Catalog, but their RPCs need the IMS BOM permission. */
export function canAccessCatalogBillOfMaterials(
  input: CatalogAccessInput,
): boolean {
  return (
    canAccessCatalog(input) &&
    (input.userPermissions?.has(ImsPermissions.ACCESS_BILL_OF_MATERIALS) ??
      false)
  );
}

/** Saved Catalog views and folders are inventory views, whose RPCs need IMS. */
export function canEditCatalogViews(
  userPermissions: ReadonlySet<UserPermissions> | undefined,
): boolean {
  return Object.values(ImsPermissions).some(
    (permission) => userPermissions?.has(permission) ?? false,
  );
}

export function useCanAccessCatalog(): boolean {
  const team = useContext(TeamContext);
  const userPermissions = useContext(PermissionsContext);
  return canAccessCatalog({ team, userPermissions });
}

export function useCanAccessCatalogBillOfMaterials(): boolean {
  const team = useContext(TeamContext);
  const userPermissions = useContext(PermissionsContext);
  return canAccessCatalogBillOfMaterials({ team, userPermissions });
}

export function useCanViewCatalogProductDetails(): boolean {
  return canViewCatalogProductDetails(useContext(PermissionsContext));
}

export function useCanEditCatalogViews(): boolean {
  return canEditCatalogViews(useContext(PermissionsContext));
}
